import { useState } from "react";
import { ImageIcon, Loader2, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { resizeImageForEmail } from "@/lib/imageResize";

function PickButton({ busy, label, onFile, testid }) {
    return (
        <label className={`inline-flex items-center justify-center gap-2 px-4 py-3 md:py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-800 transition-colors ${busy ? "opacity-50 pointer-events-none" : "cursor-pointer hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))]"}`} data-testid={testid}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? "A carregar…" : label}
            <input type="file" accept="image/*" className="hidden" disabled={busy}
                onChange={(e) => { onFile(e.target.files[0]); e.target.value = ""; }} data-testid={`${testid}-input`} />
        </label>
    );
}

/** Foto de capa da obra (leads.cover_file_id): escolhida manualmente; mesmo upload e resize das restantes fotografias. */
export function LeadCoverPhoto({ leadId, coverId, onChange }) {
    const [busy, setBusy] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) return toast.error("Escolha uma fotografia.");
        setBusy(true);
        try {
            let payload = file;
            try {
                payload = await resizeImageForEmail(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.82 });
            } catch { /* fall back to raw file */ }
            const fd = new FormData();
            fd.append("file", payload);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.put(`/admin/leads/${leadId}/cover`, { file_id: data.id });
            onChange(data.id);
            toast.success(coverId ? "Foto de capa substituída." : "Foto de capa definida.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao carregar a foto de capa");
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!window.confirm("Remover a foto de capa da obra?")) return;
        setBusy(true);
        try {
            await api.delete(`/admin/leads/${leadId}/cover`);
            onChange(null);
            toast.success("Foto de capa removida.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao remover a foto de capa");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="lead-cover">
            <div className="flex items-center gap-2 mb-2">
                <ImageIcon className="w-4 h-4 text-stone-500" />
                <span className="text-xs uppercase tracking-wider text-stone-500 font-medium">Foto de capa da obra</span>
            </div>
            {coverId ? (
                <div className="space-y-3">
                    <a href={fileUrl(coverId)} target="_blank" rel="noreferrer" className="block w-full max-w-md aspect-video rounded-lg overflow-hidden bg-stone-100 hover:ring-2 hover:ring-[hsl(var(--copper))]" data-testid="lead-cover-open">
                        <img src={fileUrl(coverId)} alt="Foto de capa da obra" className="w-full h-full object-cover" data-testid="lead-cover-img" />
                    </a>
                    <div className="flex flex-wrap items-center gap-2">
                        <PickButton busy={busy} label="Substituir" onFile={upload} testid="lead-cover-replace" />
                        <button type="button" onClick={remove} disabled={busy}
                            className="inline-flex items-center gap-2 px-4 py-3 md:py-2.5 rounded-full text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                            data-testid="lead-cover-remove">
                            <Trash2 className="w-4 h-4" /> Remover
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <p className="text-xs text-stone-500 mb-3" data-testid="lead-cover-empty">
                        Sem foto de capa. A fotografia escolhida aparece no cartão principal da Área do Cliente.
                    </p>
                    <PickButton busy={busy} label="Carregar fotografia" onFile={upload} testid="lead-cover-upload" />
                </>
            )}
        </div>
    );
}
